import { useMemo, useState } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import EmptyState from '@/components/base/EmptyState';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import MovementForm from './components/MovementForm';
import { useFinancialMovements } from '@/hooks/useFinancialMovements';
import { useProjects } from '@/hooks/useProjects';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/feature/ToastProvider';
import { deleteMovement } from '@/lib/financeService';
import { formatCurrency, formatDate } from '@/lib/format';
import type { FinancialMovement, MovementType } from '@/types';

type TypeFilter = 'TODOS' | MovementType;

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: 'TODOS', label: 'Todos' },
  { value: 'INGRESO', label: 'Ingresos' },
  { value: 'EGRESO', label: 'Egresos' },
];

function SummaryCard({
  icon,
  label,
  value,
  iconClass,
  valueClass,
}: {
  icon: string;
  label: string;
  value: string;
  iconClass: string;
  valueClass: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${iconClass}`}>
        <i className={`${icon} text-xl`}></i>
      </span>
      <div className="min-w-0">
        <p className="text-xs text-foreground-500">{label}</p>
        <p className={`mt-0.5 font-heading text-lg font-extrabold leading-none ${valueClass}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const { notify } = useToast();
  const { isSuperUsuario } = useAuth();
  const { movements, loading, error, reload } = useFinancialMovements();
  const { projects } = useProjects();

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('TODOS');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FinancialMovement | null>(null);
  const [toDelete, setToDelete] = useState<FinancialMovement | null>(null);
  const [deleting, setDeleting] = useState(false);

  const totals = useMemo(() => {
    const income = movements
      .filter((movement) => movement.movement_type === 'INGRESO')
      .reduce((sum, movement) => sum + Number(movement.amount), 0);
    const expense = movements
      .filter((movement) => movement.movement_type === 'EGRESO')
      .reduce((sum, movement) => sum + Number(movement.amount), 0);
    return { income, expense, balance: income - expense };
  }, [movements]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return movements.filter((movement) => {
      const matchType = typeFilter === 'TODOS' || movement.movement_type === typeFilter;
      const matchTerm =
        !term ||
        movement.concept.toLowerCase().includes(term) ||
        movement.category.toLowerCase().includes(term) ||
        (movement.created_by_name || '').toLowerCase().includes(term);
      return matchType && matchTerm;
    });
  }, [movements, typeFilter, search]);

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteMovement(toDelete.id);
      notify({ tone: 'success', title: 'Movimiento eliminado', message: 'El movimiento fue eliminado correctamente.' });
      setToDelete(null);
      reload();
    } catch {
      notify({ tone: 'error', title: 'No se pudo eliminar', message: 'Intente nuevamente.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon="ri-arrow-down-circle-line"
          label="Total ingresos"
          value={formatCurrency(totals.income)}
          iconClass="bg-primary-100 text-primary-700"
          valueClass="text-primary-700"
        />
        <SummaryCard
          icon="ri-arrow-up-circle-line"
          label="Total egresos"
          value={formatCurrency(totals.expense)}
          iconClass="bg-red-100 text-red-600"
          valueClass="text-red-600"
        />
        <SummaryCard
          icon="ri-wallet-3-line"
          label="Saldo actual"
          value={formatCurrency(totals.balance)}
          iconClass="bg-secondary-100 text-secondary-700"
          valueClass={totals.balance < 0 ? 'text-red-600' : 'text-foreground-950'}
        />
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">Movimientos</h2>
          <p className="text-sm text-foreground-500">{filtered.length} de {movements.length} movimientos</p>
        </div>
        <Button
          icon="ri-add-line"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          Nuevo movimiento
        </Button>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por concepto, categoría o usuario..."
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-50 px-1 py-1">
          {TYPE_FILTERS.map((filter) => {
            const active = typeFilter === filter.value;
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => setTypeFilter(filter.value)}
                className={[
                  'cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-200',
                  active
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900',
                ].join(' ')}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <Loading label="Cargando movimientos..." />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-exchange-dollar-line"
          title={movements.length === 0 ? 'No hay movimientos registrados' : 'Sin resultados'}
          description={
            movements.length === 0
              ? 'Registre su primer ingreso o egreso para comenzar el control financiero.'
              : 'Ajuste la búsqueda o el filtro aplicado.'
          }
          action={
            movements.length === 0 ? (
              <Button
                icon="ri-add-line"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Nuevo movimiento
              </Button>
            ) : (
              <Button
                variant="outline"
                icon="ri-refresh-line"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('TODOS');
                }}
              >
                Limpiar filtros
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Fecha</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Tipo</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Categoría</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Concepto</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">Monto</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Usuario</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((movement) => {
                  const isIncome = movement.movement_type === 'INGRESO';
                  return (
                    <tr key={movement.id} className="border-b border-background-200 last:border-0 transition-colors hover:bg-background-100/50">
                      <td className="px-4 py-3 text-sm text-foreground-700">{formatDate(movement.movement_date)}</td>
                      <td className="px-4 py-3">
                        <Badge
                          tone={isIncome ? 'primary' : 'danger'}
                          icon={isIncome ? 'ri-arrow-down-circle-line' : 'ri-arrow-up-circle-line'}
                        >
                          {isIncome ? 'Ingreso' : 'Egreso'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">{movement.category}</td>
                      <td className="px-4 py-3 text-sm text-foreground-800">{movement.concept}</td>
                      <td className={`px-4 py-3 text-right text-sm font-semibold ${isIncome ? 'text-primary-700' : 'text-red-600'}`}>
                        {isIncome ? '+' : '−'} {formatCurrency(movement.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">{movement.created_by_name || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(movement);
                              setFormOpen(true);
                            }}
                            aria-label="Editar movimiento"
                            title="Editar"
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
                          >
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          {isSuperUsuario && (
                            <button
                              type="button"
                              onClick={() => setToDelete(movement)}
                              aria-label="Eliminar movimiento"
                              title="Eliminar"
                              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-red-600 transition-colors hover:border-red-300 hover:bg-red-50"
                            >
                              <i className="ri-delete-bin-6-line text-base"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MovementForm
        open={formOpen}
        movement={editing}
        projects={projects}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={() => {
          setFormOpen(false);
          setEditing(null);
          reload();
        }}
      />

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="¿Está seguro de que desea eliminar este movimiento?"
        description="Esta acción no podrá deshacerse."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        tone="danger"
        icon="ri-delete-bin-6-line"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}